import { View } from "@westpierstudio/luma/mvc";
import { GameServices } from "game/GameServices";
import { SignalBinding } from "type-signals";
import { SpriteAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { Container, Graphics, Sprite } from "pixi.js";

export class FullScreenPanel extends View {
    private backgroundContainer: Container;
    private frameBars: {
        left: Sprite;
        right: Sprite;
        top: Sprite;
        bottom: Sprite;
    };
    private frameCorners: {
        topLeft: Sprite;
        topRight: Sprite;
        bottomLeft: Sprite;
        bottomRight: Sprite;
    };
    private container: Container;
    private maskGraphic: Graphics;
    private resizeBinding?: SignalBinding;
    contentContainer: Container;

    constructor(protected override readonly services: GameServices) {
        super(services);

        const {factory, resources} = this.services;

        const spec = resources.getSpec(`fullscreenpanel`);

        this.container = factory.createContainer({}, this);

        this.backgroundContainer = factory.createContainer({}, this.container);
        for (const child of Object.values(spec.background)) {
            factory.createSprite(child as SpriteAssetSpec, this.backgroundContainer);
        }

        this.contentContainer = factory.createContainer({}, this.container);

        this.frameBars = {
            left: factory.createSprite(spec.frameVertical, this.container),
            right: factory.createSprite(spec.frameVertical, this.container),
            top: factory.createSprite(spec.frameHorizontalTop, this.container),
            bottom: factory.createSprite(spec.frameHorizontalBottom, this.container),
        };

        this.frameCorners = {
            topLeft: factory.createSprite({...spec.frameCornerTop, scale: {x: 1, y: 1}, anchor: {x: 0, y: 0}}, this.container),
            topRight: factory.createSprite({...spec.frameCornerTop, scale: {x: -1, y: 1}, anchor: {x: 1, y: 0}}, this.container),
            bottomLeft: factory.createSprite({...spec.frameCornerBottom, scale: {x: 1, y: -1}, anchor: {x: 0, y: 1}}, this.container),
            bottomRight: factory.createSprite({...spec.frameCornerBottom, scale: {x: -1, y: -1}, anchor: {x: 1, y: 1}}, this.container),
        };

        this.maskGraphic = factory.createGraphics({}, this.container);
        this.maskGraphic.beginFill(0x00FF00, 1);
        this.maskGraphic.drawRect(0, 0, 1, 1);

        this.container.addChild(this.maskGraphic);
        this.backgroundContainer.mask = this.maskGraphic;
    }

    enableResize(): void {

        if (!this.resizeBinding) {
            this.resizeBinding = this.services.layout.onResize.add(this.resize, this);
        }

        this.resize();
    }

    disableResize(): void {

        if (this.resizeBinding) {

            this.services.layout.onResize.detach(this.resizeBinding);
            this.resizeBinding = undefined;
        }
    }

    private resize() {

        const bounds = this.services.layout.getScreenBoundaryInBucketSpace();
        const {width, height, top, left} = bounds;

        // proportionally scale the background, aligning by half the excess to keep it central
        if (width > height) {
            this.backgroundContainer.width = width;
            this.backgroundContainer.scale.y = this.backgroundContainer.scale.x;
            this.backgroundContainer.x = 0;
            this.backgroundContainer.y = (height - this.backgroundContainer.height) / 2;
        } else {
            this.backgroundContainer.height = height;
            this.backgroundContainer.scale.x = this.backgroundContainer.scale.y;
            this.backgroundContainer.x = (width - this.backgroundContainer.width) / 2;
            this.backgroundContainer.y = 0;
        }

        this.container.x = left;
        this.container.y = top;

        const frameThickness = this.frameCorners.topLeft.width;

        this.frameBars.left.position.set(0, frameThickness);
        this.frameBars.right.position.set(width - this.frameBars.right.width, frameThickness);
        this.frameBars.top.position.set(frameThickness, 0);
        this.frameBars.bottom.position.set(frameThickness, height - this.frameBars.bottom.height);

        const cornerSpace = frameThickness * 2;
        // scale the frame bars to the width/height
        this.frameBars.left.height = height - cornerSpace;
        this.frameBars.right.height = height - cornerSpace;
        this.frameBars.top.width = width - cornerSpace;
        this.frameBars.bottom.width = width - cornerSpace;

        this.frameCorners.topLeft.position.set(0, 0);
        this.frameCorners.topRight.position.set(width - frameThickness, 0);
        this.frameCorners.bottomLeft.position.set(0, height - frameThickness);
        this.frameCorners.bottomRight.position.set(width - frameThickness, height - frameThickness);

        this.maskGraphic.width = width;
        this.maskGraphic.height = height;

        this.contentContainer.position.set(width/2, height/2);
    }
}